/**
 * LTI 1.3 service wrappers (feedbackUI).
 *
 * Thin, framework-free API helpers for the LTI flows the SPA participates in.
 * Mirrors the conventions in `service/api.js` (`baseUrl`, `postApi`, `getApi`,
 * `fetchData`) so it can be re-exported from the monolithic `service.js`
 * barrel (see PATCHES.md) and imported by components the same way as every
 * other call.
 *
 * This PR (PR-11 / PLAN §7.2, §8.1, §11) adds the **deep-link authoring
 * return** call:
 *
 *   POST /lti/deeplink/return  { dlt, assignmentId }  ->  { jwt, returnUrl }
 *
 * The endpoint is AUTHENTICATED (SR-9 / §8.1): it is called by the teacher's
 * already-logged-in SPA session (app JWT in the Authorization header, added by
 * `postApi`/`modifyData`). It is NOT one of the five public `/lti/*` endpoints.
 *
 * The backend (PR-13, M3) validates `dlt` against `lti_pending_deeplink`,
 * writes the pending-deeplink binding for `assignmentId`, builds the
 * `LtiDeepLinkingResponse` content item, signs it (RS256, ToolKeyService), and
 * returns the signed JWT plus the platform's `deep_link_return_url`. The SPA
 * then renders an auto-submit form that POSTs `JWT` back to `returnUrl`
 * (see components/Lti/DeepLinkReturnForm.jsx) — completing the round-trip into
 * the LMS with the selected assignment as a content item.
 *
 * Contract note: `exchangeLtiCodeForToken` (the `?lti_code=` -> app session
 * handoff) is owned by PR-08 (PLAN §8.3) and lives in the main service.js; it
 * is intentionally NOT duplicated here.
 */

import { baseUrl, postApi } from './api';

/**
 * Shape returned by POST /lti/deeplink/return.
 * @typedef {Object} DeepLinkReturnResponse
 * @property {string} jwt        Signed LtiDeepLinkingResponse JWT to POST back.
 * @property {string} returnUrl  The platform's deep_link_return_url (form action).
 * @property {string} [formField] Optional override of the form field name the
 *                                platform expects (defaults to 'JWT' per spec).
 */

/**
 * Ask the backend to mint the signed deep-linking response for the assignment
 * the teacher just created or selected inside the deep-link iframe.
 *
 * @param {string} dlt           Opaque deep-link transaction id from the launch
 *                               redirect (`/tasks/new?lti_dl=1&ctx=&dlt=`).
 * @param {string} assignmentId  The Jeddle assignment UUID to return as the
 *                               content item.
 * @param {Object} [opts]
 * @param {boolean} [opts.includeMessages] When true, return the full
 *                               {data, successMessage, errorMessage} envelope
 *                               instead of just the data (mirrors postApi).
 * @returns {Promise<DeepLinkReturnResponse|null>} The signed-response payload,
 *          or null on failure (postApi swallows + toasts the error by default,
 *          matching the rest of the codebase).
 */
export const postDeepLinkReturn = async (dlt, assignmentId, opts = {}) => {
  if (!dlt) {
    throw new Error('postDeepLinkReturn: missing deep-link transaction (dlt)');
  }
  if (!assignmentId) {
    throw new Error('postDeepLinkReturn: missing assignmentId');
  }
  return postApi(
    baseUrl + '/lti/deeplink/return',
    { dlt, assignmentId },
    // We want hard failures surfaced to the caller so the authoring UI can show
    // a "couldn't return to your LMS" state rather than silently no-op; the
    // component decides whether to toast. Default behaviour returns response.data.
    { includeMessages: Boolean(opts.includeMessages) }
  );
};

export default { postDeepLinkReturn };
